import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Copy,
  ExternalLink,
  Gift,
  Link as LinkIcon,
  Loader,
  MousePointerClick,
  Share2,
  Timer,
  Users,
  Calendar,
  SearchX,
} from "lucide-react";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Modal from "../../components/common/Modal";
import Toast from "../../components/common/Toast";
import { useUpdateWaitlist } from "../../customHooks/trip";
import { useGlobalState } from "../../GlobalStates";
import { Footer, StickyHeader } from "../../intro/components/landing-new";
import { getCampaignDetailsBySlug } from "../../services/campaign";
import { useIsAuthenticated } from "../../utils/auth";
import { formatCurrency } from "../../utils/currency";
import dayjs from "dayjs";
import Markdown from "markdown-to-jsx";
import { convertUTCToLocalDateTimeString } from "../../utils/utils";
import { useTabletOrMobile, useIsDesktop } from "../../hooks/responsive";
import { AutoPlaySilentVideo } from "../../components/video";
import { useTranslation } from "react-i18next";
import { landingVideoDesktop, landingVideoMobile } from "../../utils/constants";
import { ImageResizable } from "../../components/elements/ImageResizeable";
import { dateRangeFormattingShortYear } from "../../utils/utils";
import { useUserLocale } from "../../customHooks/useUserLocale";
// Utility function to check if a campaign is in pre-launch state
const isPreLaunchCampaign = (campaign, checkLaunchMethod = false) => {
  if (!campaign) return false;

  // If we need to specifically check launch method
  if (
    checkLaunchMethod &&
    (!campaign.launch_time || campaign.launch_method !== "early_access")
  ) {
    return false;
  }

  if (campaign.launch_time) {
    return dayjs().isBefore(dayjs(campaign.launch_time));
  }

  if (campaign.publicSaleStartTime) {
    return dayjs().isBefore(dayjs(campaign.publicSaleStartTime));
  }

  return false;
};

// Helper function to generate login redirect URL
const getLoginRedirectUrl = () => {
  const originalPath = window.location.pathname + window.location.search;
  return `/login?reroute=${encodeURIComponent(originalPath)}`;
};

const WaitlistModal = ({
  waitlisters,
  totalCount,
  tripTitle,
  onClose,
  onShowToast,
  trip,
  onJoinWaitlist,
}) => {
  const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
  const { isAuthenticated } = useIsAuthenticated();
  const [user] = useGlobalState("user");
  const { t } = useTranslation();

  // Check if the current user has already joined the waitlist
  const hasJoinedWaitlist = useMemo(() => {
    if (!isAuthenticated || !user || !waitlisters) return false;
    return waitlisters.some((waitlister) => waitlister.id === user.id);
  }, [isAuthenticated, user, waitlisters]);

  const handleJoinWaitlist = async () => {
    if (!isAuthenticated) {
      window.location = getLoginRedirectUrl();
      return;
    }

    setIsJoiningWaitlist(true);

    try {
      // Use the actual API integration
      await onJoinWaitlist();
      onShowToast(t("campaign.waitlist.successfully_joined"), "success");
      onClose();
    } catch (error) {
      console.error("Error joining waitlist:", error);
      onShowToast(
        error.message || t("campaign.waitlist.failed_to_join"),
        "error"
      );
    } finally {
      setIsJoiningWaitlist(false);
    }
  };

  const headerContent = (
    <div>
      <h2 className="tw-text-2xl tw-font-bold tw-bg-gradient-to-r tw-from-gray-900 tw-to-gray-600 tw-bg-clip-text tw-text-transparent">
        {t("campaign.waitlist.for_title", { tripTitle })}
      </h2>
      <div className="tw-flex tw-items-center tw-space-x-2 tw-mt-2">
        {totalCount > 0 && (
          <>
            <Users className="tw-w-5 tw-h-5 tw-text-[deepskyblue]" />{" "}
            <span className="tw-text-gray-600">
              {totalCount}{" "}
              {totalCount === 1
                ? t("campaign.waitlist.person")
                : t("campaign.waitlist.people")}{" "}
              {t("campaign.waitlist.waitlisted")}
            </span>
          </>
        )}
      </div>
    </div>
  );

  const footerContent = hasJoinedWaitlist ? (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
      <div className="tw-flex tw-items-center tw-space-x-2 tw-text-emerald-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="tw-w-5 tw-h-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
        <span className="tw-font-medium">
          {t("campaign.waitlist.youre_on_waitlist")}
        </span>
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClose}
        className="tw-bg-gray-100 tw-text-gray-700 tw-rounded-xl tw-py-2 tw-px-4 tw-font-medium hover:tw-bg-gray-200 tw-transition-colors"
      >
        {t("common.close")}
      </motion.button>
    </div>
  ) : isAuthenticated ? (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleJoinWaitlist}
      disabled={isJoiningWaitlist}
      className="tw-w-full tw-bg-[deepskyblue] tw-text-white tw-rounded-xl tw-py-3 tw-font-medium tw-flex tw-items-center tw-justify-center tw-space-x-2 hover:tw-bg-[#00a6e6] tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
    >
      {isJoiningWaitlist ? (
        <>
          <Loader className="tw-w-5 tw-h-5 tw-animate-spin" />
          <span>{t("campaign.waitlist.joining")}</span>
        </>
      ) : (
        <>
          <span>{t("campaign.waitlist.join_waitlist")}</span>
          <ChevronRight className="tw-w-5 tw-h-5" />
        </>
      )}
    </motion.button>
  ) : (
    <motion.a
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      href={getLoginRedirectUrl()}
      className="tw-w-full tw-bg-gray-200 tw-text-gray-700 tw-rounded-xl tw-py-3 tw-font-medium tw-flex tw-items-center tw-justify-center tw-space-x-2 hover:tw-bg-gray-300 tw-transition-colors"
    >
      <span>{t("common.sign_in_to_waitlist")}</span>
      <ChevronRight className="tw-w-5 tw-h-5" />
    </motion.a>
  );

  return (
    <Modal
      onClose={onClose}
      headerContent={headerContent}
      footerContent={footerContent}
    >
      <div className="tw-flex-1 tw-overflow-auto">
        {totalCount > 0 ? (
          <div className="tw-overflow-y-auto tw-max-h-[60vh] tw-pr-2 tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent">
            <div className="tw-space-y-4">
              {waitlisters.map((waitlister, index) => (
                <motion.div
                  key={waitlister.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.03, 0.5) }}
                  className={`tw-flex tw-items-center tw-space-x-4 tw-p-4 tw-rounded-xl tw-bg-gray-50 hover:tw-bg-gray-100 tw-transition-colors ${
                    isAuthenticated && user && waitlister.id === user.id
                      ? "tw-border-emerald-200 tw-bg-emerald-50"
                      : ""
                  }`}
                >
                  <ImageResizable
                    size="xs"
                    src={waitlister.avatar_url}
                    alt={waitlister.name}
                    className="tw-w-12 tw-h-12 tw-rounded-full tw-object-cover tw-border-2 tw-border-white"
                    loading="lazy"
                  />
                  <div className="tw-flex-1">
                    <div className="tw-font-medium tw-text-gray-900">
                      {waitlister.name}
                      {isAuthenticated && user && waitlister.id === user.id && (
                        <span className="tw-ml-2 tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-emerald-100 tw-text-emerald-600">
                          {t("campaign.waitlist.you")}
                        </span>
                      )}
                    </div>
                    <div className="tw-text-sm tw-text-gray-500">
                      {t("campaign.waitlist.joined")}{" "}
                      {new Date(waitlister.joinedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="tw-text-xs tw-font-medium tw-px-3 tw-py-1 tw-rounded-full tw-bg-sky-50 tw-text-[deepskyblue]">
                    #{index + 1}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tw-py-6 tw-flex tw-flex-col tw-items-center tw-justify-center"
          >
            <div className="tw-relative tw-mb-8">
              <div className="tw-w-32 tw-h-32 tw-bg-gradient-to-br tw-from-sky-100 tw-to-blue-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                <Users className="tw-w-16 tw-h-16 tw-text-[deepskyblue]" />
              </div>
              <motion.div
                animate={{
                  y: [0, -5, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
                className="tw-absolute -tw-top-2 -tw-right-2 tw-bg-amber-400 tw-text-white tw-rounded-full tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-shadow-lg"
              >
                <span className="tw-text-lg tw-font-bold">1</span>
              </motion.div>
            </div>

            <h3 className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-3 tw-text-center">
              {t("campaign.waitlist.join_the_waitlist")}
            </h3>

            <p className="tw-text-gray-600 tw-text-center tw-max-w-md tw-mb-6">
              {t("campaign.waitlist.get_early_access", { tripTitle })}
            </p>

            <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-4 tw-mb-6">
              <div className="tw-flex tw-items-center tw-bg-gray-50 tw-rounded-xl tw-p-3">
                <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-emerald-100 tw-flex tw-items-center tw-justify-center tw-mr-3">
                  <Timer className="tw-w-4 tw-h-4 tw-text-emerald-600" />
                </div>
                <span className="tw-text-sm tw-text-gray-700">
                  {t("campaign.waitlist.early_bird_deal")}
                </span>
              </div>

              <div className="tw-flex tw-items-center tw-bg-gray-50 tw-rounded-xl tw-p-3">
                <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-amber-100 tw-flex tw-items-center tw-justify-center tw-mr-3">
                  <AlertTriangle className="tw-w-4 tw-h-4 tw-text-amber-600" />
                </div>
                <span className="tw-text-sm tw-text-gray-700">
                  {t("campaign.waitlist.limited_spots")}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </Modal>
  );
};

const WaitlistersPreview = ({
  waitlisters,
  totalCount,
  tripTitle,
  onShowAll,
}) => {
  const { t } = useTranslation();
  const displayCount = 5;
  const displayedWaitlisters = waitlisters.slice(0, displayCount);
  const [user] = useGlobalState("user");
  const { isAuthenticated } = useIsAuthenticated();

  // Check if the current user has already joined the waitlist
  const hasJoinedWaitlist = useMemo(() => {
    if (!isAuthenticated || !user || !waitlisters) return false;
    return waitlisters.some((waitlister) => waitlister.id === user.id);
  }, [isAuthenticated, user, waitlisters]);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      onClick={onShowAll}
      className="tw-flex tw-items-center tw-space-x-3 tw-cursor-pointer"
    >
      <div className="tw-flex -tw-space-x-2">
        {displayedWaitlisters.map((waitlister, index) => (
          <motion.div
            key={waitlister.id}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2, delay: index * 0.1, ease: "easeOut" }}
            className={`tw-w-8 tw-h-8 tw-rounded-full tw-border-2 tw-overflow-hidden tw-relative tw-group ${
              isAuthenticated && user && waitlister.id === user.id
                ? "tw-border-emerald-300 tw-ring-2 tw-ring-emerald-200"
                : "tw-border-white"
            }`}
          >
            <ImageResizable
              size="xs"
              src={waitlister.avatar_url}
              alt={waitlister.name}
              className="tw-w-full tw-h-full tw-object-cover"
            />
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileHover={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="tw-absolute tw-invisible tw-group-hover:tw-visible tw-bg-black/75 tw-backdrop-blur-sm tw-text-white tw-px-2 tw-py-1 tw-rounded-lg tw-text-xs tw-whitespace-nowrap tw-bottom-full tw-left-1/2 tw-transform -tw-translate-x-1/2 -tw-translate-y-2 tw-z-10"
            >
              {waitlister.name}
              {isAuthenticated && user && waitlister.id === user.id && (
                <span className="tw-ml-1 tw-text-emerald-300">(You)</span>
              )}
              <div className="tw-text-[10px] tw-text-gray-300">
                {t("campaign.waitlist.joined")}{" "}
                {new Date(waitlister.joinedAt).toLocaleDateString()}
              </div>
            </motion.div>
          </motion.div>
        ))}
        {totalCount > displayCount && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2, delay: displayCount * 0.1 }}
            className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-100 tw-border-2 tw-border-white tw-flex tw-items-center tw-justify-center tw-relative tw-group"
          >
            <span className="tw-text-xs tw-font-medium tw-text-gray-600">
              +{totalCount - displayCount}
            </span>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileHover={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="tw-absolute tw-invisible tw-group-hover:tw-visible tw-bg-black/75 tw-backdrop-blur-sm tw-text-white tw-px-2 tw-py-1 tw-rounded-lg tw-text-xs tw-whitespace-nowrap tw-bottom-full tw-left-1/2 tw-transform -tw-translate-x-1/2 -tw-translate-y-2 tw-z-10"
            >
              {totalCount - displayCount}{" "}
              {t("campaign.waitlist.more_waitlisters")}
            </motion.div>
          </motion.div>
        )}
      </div>
      <div className="tw-flex tw-flex-col">
        <span className="tw-text-sm tw-font-medium tw-text-gray-900">
          {totalCount} {t("campaign.waitlist.waitlisted")}
          {hasJoinedWaitlist && (
            <span className="tw-ml-1 tw-text-xs tw-font-medium tw-text-emerald-600">
              {t("campaign.waitlist.including_you")}
            </span>
          )}
        </span>
      </div>
    </motion.div>
  );
};

const TripCard = React.memo(
  ({
    trip,
    onShowToast,
    onJoinWaitlist,
    cardHeight,
    onShowWaitlistModal,
    campaign,
    promotion,
  }) => {
    const [{ projects: tProjects }] = useGlobalState("translations");
    const { t } = useTranslation();

    const projectId = trip.project_id;

    const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const { isAuthenticated } = useIsAuthenticated();
    const [user] = useGlobalState("user");
    const descriptionRef = useRef(null);
    const [isDescriptionLong, setIsDescriptionLong] = useState(false);

    // Check if the current user has already joined the waitlist
    const hasJoinedWaitlist = useMemo(() => {
      if (!isAuthenticated || !user || !trip.waitlisters) return false;
      return trip.waitlisters.some((waitlister) => waitlister.id === user.id);
    }, [isAuthenticated, user, trip.waitlisters]);

    // Check if the campaign is still in pre-launch state
    const isPreLaunch = useMemo(
      () => isPreLaunchCampaign(campaign),
      [campaign]
    );

    // Check if description is long on mount
    useEffect(() => {
      if (descriptionRef.current) {
        const isLong = descriptionRef.current.scrollHeight > 80; // 4 lines of text approximately
        setIsDescriptionLong(isLong);
      }
    }, [trip.description]);

    const handleJoinWaitlist = async () => {
      setIsJoiningWaitlist(true);

      try {
        await onJoinWaitlist();
      } catch (error) {
        console.error("Error in handleJoinWaitlist:", error);
      } finally {
        setIsJoiningWaitlist(false);
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-transition-all tw-overflow-hidden tw-group tw-flex tw-flex-col"
        style={{ height: cardHeight }}
      >
        <div className="tw-relative">
          <ImageResizable
            size="oxl"
            src={trip.image}
            alt={trip.title}
            className="tw-w-full tw-h-64 tw-object-cover"
          />
          <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/70 tw-via-black/20 tw-to-transparent"></div>

          {/* Display user images overlay if available */}
          {trip.three_user_images && trip.three_user_images.length > 0 && (
            <div className="tw-absolute tw-top-4 tw-left-4 tw-flex -tw-space-x-2">
              {trip.three_user_images.slice(0, 3).map((img, idx) => (
                <div
                  key={idx}
                  className="tw-w-8 tw-h-8 tw-rounded-full tw-border-2 tw-border-white tw-overflow-hidden"
                >
                  <ImageResizable
                    size="xs"
                    src={img}
                    alt="User"
                    className="tw-w-full tw-h-full tw-object-cover"
                  />
                </div>
              ))}
            </div>
          )}

          <div className="tw-absolute tw-top-4 tw-right-4">
            <div className="tw-bg-white/90 tw-backdrop-blur-sm tw-rounded-xl tw-px-3 tw-py-1.5 tw-flex tw-items-center tw-space-x-1">
              <Clock className="tw-w-4 tw-h-4 tw-text-[deepskyblue]" />
              <span className="tw-text-sm tw-font-medium">
                {tProjects[projectId]?.duration || trip.duration}
              </span>
            </div>
          </div>
          <div className="tw-absolute tw-bottom-4 tw-left-4 tw-right-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-gap-2">
              <div>
                <h3 className="tw-text-white tw-font-bold tw-text-xl tw-mb-1 tw-line-clamp-2">
                  {tProjects[projectId]?.project_name || trip.title}
                </h3>
                <div className="tw-flex tw-items-center tw-space-x-2 tw-mb-2">
                  {isPreLaunch && (
                    <>
                      <div className="tw-flex tw-items-center tw-space-x-1">
                        <Users className="tw-w-4 tw-h-4 tw-text-white/80" />
                        <span className="tw-text-white/80 tw-text-sm">
                          {trip.waitlistCount}{" "}
                          {t("campaign.waitlist.waitlisted")}
                        </span>
                      </div>
                      <span className="tw-text-white/60">•</span>
                    </>
                  )}
                  <span className="tw-text-white/80 tw-text-sm">
                    {tProjects[projectId]?.city || trip.city}
                  </span>
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-items-end tw-min-w-[100px]">
                {promotion?.discountedTripIdToPromoObject[trip?.id]
                  ?.special_discount_percentage > 0 ? (
                  <div className="tw-flex tw-flex-col tw-items-end">
                    <span className="tw-text-white/70 tw-font-medium tw-text-xl tw-line-through">
                      {formatCurrency(trip.price)}
                    </span>
                    <span className="tw-text-white tw-font-bold tw-text-xl tw-whitespace-nowrap tw-text-[lightgreen]">
                      {formatCurrency(
                        Math.ceil(
                          (trip.price *
                            (100 -
                              promotion?.discountedTripIdToPromoObject[trip?.id]
                                ?.special_discount_percentage)) /
                            100
                        )
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="tw-text-white tw-font-bold tw-text-xl tw-whitespace-nowrap">
                    {formatCurrency(trip.price)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="tw-p-4 tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden">
          <div className="tw-flex-1 tw-overflow-y-auto tw-pr-2 tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent">
            {/* Display local host if available */}
            {/* {trip.local_host && (
              <div className="tw-flex tw-items-center tw-mb-3">
                <div className="tw-relative tw-mr-3">
                  <img
                    src={
                      trip.local_host.image ||
                      `https://i.pravatar.cc/150?img=${Math.floor(
                        Math.random() * 70
                      )}`
                    }
                    alt={trip.local_host.first_name}
                    className="tw-w-8 tw-h-8 tw-rounded-full tw-object-cover"
                  />
                  <div className="tw-absolute -tw-bottom-1 -tw-right-1 tw-w-4 tw-h-4 tw-bg-emerald-500 tw-rounded-full tw-border-2 tw-border-white"></div>
                </div>
                <div>
                  <p className="tw-text-xs tw-text-gray-500">
                    {t("campaign.trip_card.our_partner")}
                  </p>
                  <p className="tw-text-sm tw-font-medium">
                    {trip.local_host.first_name ||
                      t("campaign.trip_card.local_host")}
                  </p>
                </div>
              </div>
            )} */}

            <div className="tw-mb-4 tw-relative">
              <div className={`tw-relative ${expanded ? "tw-pb-8" : ""}`}>
                <div
                  ref={descriptionRef}
                  className={`tw-text-gray-600 tw-text-sm tw-whitespace-pre-line ${
                    !expanded && isDescriptionLong ? "tw-line-clamp-3" : ""
                  }`}
                >
                  {(tProjects[projectId]?.summary || trip.description)
                    .split("\n")
                    .map(
                      (paragraph, index) =>
                        paragraph.trim() && (
                          <p
                            key={index}
                            style={{ marginTop: index > 0 ? "8px" : "0" }}
                          >
                            <Markdown
                              options={{
                                overrides: {
                                  a: {
                                    component: ({ children }) => (
                                      <span
                                        style={{
                                          fontWeight: "bold",
                                          textDecoration: "underline",
                                        }}
                                      >
                                        {children}
                                      </span>
                                    ),
                                  },
                                },
                              }}
                            >
                              {paragraph}
                            </Markdown>
                          </p>
                        )
                    )}
                </div>
                {isDescriptionLong && !expanded && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setExpanded(!expanded)}
                    className={`tw-text-[deepskyblue] tw-text-xs tw-font-medium tw-mt-1 ${
                      expanded
                        ? "tw-absolute tw-bottom-0 tw-right-0 tw-bg-white tw-px-2 tw-py-1 tw-rounded-md tw-shadow-sm"
                        : ""
                    }`}
                  >
                    {expanded
                      ? t("campaign.trip_card.read_less")
                      : t("campaign.trip_card.read_more")}
                  </motion.button>
                )}
              </div>
            </div>

            <div className="tw-mb-4">
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {trip.highlights &&
                  trip.highlights.map((highlight, index) => (
                    <span
                      key={index}
                      className="tw-bg-sky-50 tw-text-[deepskyblue] tw-text-xs tw-font-medium tw-px-2.5 tw-py-1 tw-rounded-full"
                    >
                      {tProjects[projectId]?.includes[index]?.title ||
                        highlight}
                    </span>
                  ))}
              </div>
            </div>

            {/* Trip Date Section */}
            <div className="tw-mb-4 tw-bg-gray-50 tw-rounded-xl tw-p-3">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                  {t("campaign.trip_card.trip_date")}
                </span>
              </div>
              <div className="tw-space-y-2">
                <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                  <div className="tw-flex tw-items-center tw-space-x-2">
                    <div className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-[deepskyblue]"></div>
                    <span className="tw-text-gray-600">
                      {dateRangeFormattingShortYear(
                        trip.startDate,
                        trip.endDate
                      )}
                    </span>
                  </div>
                  <div className="tw-flex tw-items-center tw-space-x-2">
                    {trip.soldOut ? (
                      <span className="tw-text-xs tw-bg-red-100 tw-text-red-600 tw-px-2 tw-py-0.5 tw-rounded-full">
                        {t("campaign.trip_card.sold_out")}
                      </span>
                    ) : trip.spotsLeft < 5 ? (
                      <span className="tw-text-xs tw-bg-amber-100 tw-text-amber-600 tw-px-2 tw-py-0.5 tw-rounded-full">
                        {trip.spotsLeft} {t("campaign.trip_card.left")}
                      </span>
                    ) : (
                      <span className="tw-text-xs tw-bg-emerald-100 tw-text-emerald-600 tw-px-2 tw-py-0.5 tw-rounded-full">
                        {t("campaign.trip_card.available")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="tw-border-t tw-border-gray-100 tw-pt-4 tw-mt-2">
            {isPreLaunch && (
              <div className="tw-mb-4">
                <WaitlistersPreview
                  waitlisters={trip.waitlisters}
                  totalCount={trip.waitlistCount}
                  tripTitle={trip.title}
                  onShowAll={() => onShowWaitlistModal(trip)}
                />
              </div>
            )}

            <div className="tw-flex tw-gap-3">
              <motion.a
                href={`${window.location.origin}/trips/${trip.project_id}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`tw-bg-gray-100 tw-text-gray-700 tw-rounded-xl tw-py-3 tw-font-medium tw-flex tw-items-center tw-justify-center tw-space-x-2 hover:tw-bg-gray-200 tw-transition-colors ${
                  isPreLaunch ? "tw-flex-1" : "tw-w-full"
                }`}
              >
                <span>{t("campaign.trip_card.explore")}</span>
                <ChevronRight className="tw-w-5 tw-h-5" />
              </motion.a>

              {isPreLaunch && (
                <>
                  {hasJoinedWaitlist ? (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className="tw-flex-1 tw-bg-emerald-100 tw-text-emerald-700 tw-rounded-xl tw-py-3 tw-font-medium tw-flex tw-items-center tw-justify-center tw-space-x-2"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="tw-w-5 tw-h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      <span>{t("campaign.trip_card.waitlisted")}</span>
                    </motion.div>
                  ) : isAuthenticated ? (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleJoinWaitlist}
                      disabled={isJoiningWaitlist}
                      className="tw-flex-1 tw-bg-[deepskyblue] tw-text-white tw-rounded-xl tw-py-3 tw-font-medium tw-flex tw-items-center tw-justify-center tw-space-x-2 hover:tw-bg-[#00a6e6] tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                      {isJoiningWaitlist ? (
                        <>
                          <Loader className="tw-w-5 tw-h-5 tw-animate-spin" />
                          <span>{t("campaign.waitlist.joining")}</span>
                        </>
                      ) : (
                        <>
                          <span>{t("campaign.waitlist.join_waitlist")}</span>
                          <ChevronRight className="tw-w-5 tw-h-5" />
                        </>
                      )}
                    </motion.button>
                  ) : (
                    <motion.a
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      href={getLoginRedirectUrl()}
                      className="tw-flex-1 tw-bg-gray-200 tw-text-gray-700 tw-rounded-xl tw-py-3 tw-font-medium tw-flex tw-items-center tw-justify-center tw-space-x-2 hover:tw-bg-gray-300 tw-transition-colors"
                    >
                      <span>{t("common.sign_in_to_waitlist")}</span>
                      <ChevronRight className="tw-w-5 tw-h-5" />
                    </motion.a>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }
);

const SaleTimer = ({ campaign }) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState("");
  const [saleStatus, setSaleStatus] = useState("upcoming"); // upcoming, early, public

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = dayjs();

      // Handle campaigns without launch_time or early access
      if (!campaign.launch_time || campaign.launch_method !== "early_access") {
        // Fall back to publicSaleStartTime if available
        if (campaign.publicSaleStartTime) {
          const publicSale = dayjs(campaign.publicSaleStartTime);
          if (now.isAfter(publicSale)) {
            setSaleStatus("public");
            return t("campaign.timer.public_sale_live");
          } else {
            setSaleStatus("upcoming");
            const diff = publicSale.diff(now);
            const minutes = Math.floor((diff / 1000 / 60) % 60);
            const hours = Math.floor((diff / 1000 / 60 / 60) % 24);
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));

            if (days > 0) {
              return t("campaign.timer.sale_starts_in", { days, hours });
            }
            return t("campaign.timer.sale_starts_in_hours", { hours, minutes });
          }
        }
        setSaleStatus("public");
        return t("campaign.timer.sale_live");
      }

      const launchTime = dayjs(campaign.launch_time);

      // Calculate early access time - 2 hours before launch
      const earlyAccessTime = launchTime.subtract(2, "hours");

      if (now.isAfter(launchTime) || now.isSame(launchTime)) {
        setSaleStatus("public");
        return t("campaign.timer.public_sale_live");
      } else if (now.isAfter(earlyAccessTime) || now.isSame(earlyAccessTime)) {
        setSaleStatus("early");
        const diff = launchTime.diff(now);
        const minutes = Math.floor((diff / 1000 / 60) % 60);
        const hours = Math.floor((diff / 1000 / 60 / 60) % 24);
        return t("campaign.timer.public_sale_in", { hours, minutes });
      } else {
        setSaleStatus("upcoming");
        const diff = earlyAccessTime.diff(now);
        const minutes = Math.floor((diff / 1000 / 60) % 60);
        const hours = Math.floor((diff / 1000 / 60 / 60) % 24);
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        // If more than 24 hours away, show days
        if (days > 0) {
          return t("campaign.timer.early_access_in", { days, hours });
        }
        return t("campaign.timer.early_access_in_hours", { hours, minutes });
      }
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 60000);
    return () => clearInterval(timer);
  }, [campaign, t]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
      className={`tw-fixed tw-bottom-4 tw-left-1/2 -tw-translate-x-1/2 tw-z-50 tw-px-8 tw-py-3 tw-rounded-full tw-shadow-lg tw-flex tw-items-center tw-space-x-3 tw-w-[300px] tw-justify-center ${
        saleStatus === "public"
          ? "tw-bg-emerald-500"
          : saleStatus === "early"
          ? "tw-bg-[deepskyblue]"
          : "tw-bg-gray-900"
      }`}
    >
      <Timer className="tw-w-5 tw-h-5 tw-text-white" />
      <span className="tw-text-white tw-font-medium">{timeLeft}</span>
    </motion.div>
  );
};

const CampaignExplainer = ({ campaign, community }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="tw-relative tw-bg-gradient-to-br tw-from-sky-500 tw-to-blue-600 tw-rounded-3xl tw-shadow-xl tw-p-10 tw-mb-12 tw-overflow-hidden"
    >
      {/* Background pattern */}
      <div className="tw-absolute tw-inset-0 tw-bg-grid-white/[0.2] tw-opacity-20" />

      <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-text-center tw-mb-12">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring" }}
          className="tw-bg-white tw-text-[deepskyblue] tw-px-6 tw-py-2 tw-rounded-full tw-text-sm tw-font-semibold tw-mb-6 tw-shadow-lg"
        >
          {t("campaign.explainer.how_it_works")}
        </motion.div>
        <h2 className="tw-text-3xl md:tw-text-4xl tw-font-bold tw-text-white tw-mb-4">
          {t("campaign.explainer.early_access")}
        </h2>
        <p className="tw-text-sky-100 tw-max-w-2xl tw-text-lg">
          {t("campaign.explainer.referral_system")}
        </p>
      </div>

      <div className="tw-grid md:tw-grid-cols-3 tw-gap-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-2xl tw-p-6 tw-flex tw-flex-col tw-items-center tw-text-center tw-group hover:tw-bg-white/20 tw-transition-all"
        >
          <div className="tw-w-16 tw-h-16 tw-bg-white tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-mb-4 tw-shadow-lg tw-group-hover:tw-scale-110 tw-transition-transform">
            <Users className="tw-w-8 tw-h-8 tw-text-[deepskyblue]" />
          </div>
          <h3 className="tw-font-semibold tw-mb-3 tw-text-white tw-text-lg">
            1. {t("campaign.explainer.join_waitlist")}
          </h3>
          <p className="tw-text-sky-100 tw-text-sm">
            {t("campaign.explainer.pick_favorites")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-2xl tw-p-6 tw-flex tw-flex-col tw-items-center tw-text-center tw-group hover:tw-bg-white/20 tw-transition-all"
        >
          <div className="tw-w-16 tw-h-16 tw-bg-white tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-mb-4 tw-shadow-lg tw-group-hover:tw-scale-110 tw-transition-transform">
            <Share2 className="tw-w-8 tw-h-8 tw-text-[deepskyblue]" />
          </div>
          <h3 className="tw-font-semibold tw-mb-3 tw-text-white tw-text-lg">
            2. {t("campaign.explainer.refer_friends")}
          </h3>
          <p className="tw-text-sky-100 tw-text-sm">
            {t("campaign.explainer.share_link")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-2xl tw-p-6 tw-flex tw-flex-col tw-items-center tw-text-center tw-group hover:tw-bg-white/20 tw-transition-all"
        >
          <div className="tw-w-16 tw-h-16 tw-bg-white tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-mb-4 tw-shadow-lg tw-group-hover:tw-scale-110 tw-transition-transform">
            <Timer className="tw-w-8 tw-h-8 tw-text-[deepskyblue]" />
          </div>
          <h3 className="tw-font-semibold tw-mb-3 tw-text-white tw-text-lg">
            3. {t("campaign.explainer.one_hour_headstart")}
          </h3>
          <p className="tw-text-sky-100 tw-text-sm">
            {t("campaign.explainer.secret_link")}{" "}
            {convertUTCToLocalDateTimeString(
              campaign.publicSaleStartTime,
              community
            )}
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="tw-mt-12 tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-justify-center tw-gap-3"
      >
        <AlertTriangle className="tw-w-5 tw-h-5 tw-text-amber-300" />
        <p className="tw-text-white tw-text-sm">
          <span className="tw-font-medium">
            {t("campaign.explainer.spots_limited")}
          </span>
        </p>
      </motion.div>
    </motion.div>
  );
};

const ScrollIndicator = () => {
  const { t } = useTranslation();
  const scrollToTrips = () => {
    document.getElementById("available-trips").scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Add state to track if we should show the indicator
  const [showIndicator, setShowIndicator] = useState(true);

  // Add effect to handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const tripsSection = document.getElementById("available-trips");
      if (tripsSection) {
        const tripsSectionPosition = tripsSection.getBoundingClientRect().top;
        // Show indicator if trips section is below viewport
        setShowIndicator(tripsSectionPosition > window.innerHeight);
      }
    };

    // Initial check
    handleScroll();

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: showIndicator ? 1 : 0 }}
      transition={{ delay: 0.6 }}
      onClick={scrollToTrips}
      className={`tw-fixed tw-bottom-20 tw-left-1/2 -tw-translate-x-1/2 tw-z-40 tw-flex tw-flex-col tw-items-center tw-gap-2 tw-cursor-pointer hover:tw-scale-105 tw-transition-transform ${
        !showIndicator ? "tw-pointer-events-none" : ""
      }`}
    >
      <motion.p
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="tw-text-sm tw-font-medium tw-text-gray-500 tw-bg-white/80 tw-backdrop-blur-sm tw-px-4 tw-py-2 tw-rounded-full tw-shadow-sm"
      >
        {t("campaign.trips.scroll_to_see_trips")}
      </motion.p>
      <motion.div
        animate={{ y: [0, 5, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="tw-bg-white/80 tw-backdrop-blur-sm tw-rounded-full tw-shadow-sm tw-p-2"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="tw-h-6 tw-w-6 tw-text-[deepskyblue]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </motion.div>
    </motion.div>
  );
};

// Empty state component for when no trips are available
const EmptyTripsState = ({ campaign }) => {
  const { t } = useTranslation();
  const isPreLaunch = useMemo(() => isPreLaunchCampaign(campaign), [campaign]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-px-4 tw-bg-gray-50 tw-rounded-2xl tw-shadow-sm tw-transition-all tw-text-center"
    >
      <div className="tw-mb-6 tw-relative">
        <div className="tw-w-24 tw-h-24 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
          <SearchX className="tw-w-12 tw-h-12 tw-text-gray-400" />
        </div>
        {isPreLaunch && (
          <motion.div
            animate={{
              y: [0, -5, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="tw-absolute -tw-top-2 -tw-right-2 tw-bg-[deepskyblue] tw-text-white tw-rounded-full tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-shadow-lg"
          >
            <Calendar className="tw-w-5 tw-h-5" />
          </motion.div>
        )}
      </div>

      <h3 className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-3">
        {isPreLaunch
          ? t("campaign.trips.no_trips_yet") || "No trips available yet"
          : t("campaign.trips.no_trips_available") || "No trips available"}
      </h3>

      <p className="tw-text-gray-600 tw-max-w-md tw-mb-6">
        {isPreLaunch
          ? t("campaign.trips.check_back_later") ||
            "Check back later for exciting new trips coming to this campaign."
          : t("campaign.trips.all_trips_sold_out") ||
            "It looks like all trips for this campaign have sold out or are no longer available."}
      </p>

      <motion.a
        href="/"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="tw-bg-[deepskyblue] tw-text-white tw-rounded-xl tw-py-3 tw-px-6 tw-font-medium tw-flex tw-items-center tw-justify-center tw-space-x-2 hover:tw-bg-[#00a6e6] tw-transition-colors"
      >
        <span>
          {t("campaign.page.explore_other_campaigns") ||
            "Explore other campaigns"}
        </span>
        <ChevronRight className="tw-w-5 tw-h-5" />
      </motion.a>
    </motion.div>
  );
};

const Campaign = () => {
  const { t } = useTranslation();
  const { language, community, currency, loading, handleChangeLocale } =
    useUserLocale();
  const [{ promotions: tPromotions }] = useGlobalState("translations");
  const [promotion, setPromotion] = useGlobalState("promotion");

  const isMobile = useTabletOrMobile();
  const isDesktop = useIsDesktop();

  const { campaignSlug } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [trips, setTrips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [user] = useGlobalState("user");
  const updateWaitlist = useUpdateWaitlist();

  // Add state for modals at the root level
  const [activeWaitlistTrip, setActiveWaitlistTrip] = useState(null);

  const { isAuthenticated, isLoading: userLoading } = useIsAuthenticated();
  const [error, setError] = useState(null);
  const [cardHeights, setCardHeights] = useState({});

  // Helper function to add earlyAccessTiers to campaign data
  const addEarlyAccessTiers = (campaignData) => {
    // If campaign doesn't have launch_time or launch_method, return original data with default tier
    if (
      !campaignData.launch_time ||
      campaignData.launch_method !== "early_access"
    ) {
      return {
        ...campaignData,
        earlyAccessTiers: [
          {
            requiredReferrals: 2,
            startTime: campaignData.publicSaleStartTime,
            perks: [
              "Early access to booking",
              // `${
              //   campaignData.percentage_or_absolute === "percentage"
              //     ? `${campaignData.discount}% off`
              //     : `$${campaignData.discount} off`
              // }`,
            ],
            achieved: campaignData.userReferrals >= 2,
          },
        ],
      };
    }

    const launchTime = dayjs(campaignData.launch_time);

    // Calculate early access time - 2 hours before launch
    const earlyAccessTime = launchTime.subtract(2, "hours");

    return {
      ...campaignData,
      // Add publicSaleStartTime for backward compatibility
      publicSaleStartTime: campaignData.launch_time,
      earlyAccessTiers: [
        {
          requiredReferrals: 2, // Only one tier that requires 3 referrals
          startTime: earlyAccessTime.toISOString(),
          perks: [
            "Early access 2 hours before public launch",
            // `${
            //   campaignData.percentage_or_absolute === "percentage"
            //     ? `${campaignData.discount}% off`
            //     : `$${campaignData.discount} off`
            // }`,
          ],
          achieved: campaignData.userReferrals >= 2,
        },
      ],
    };
  };

  // Format date range for display - moved from ProjectCard to be used by TripsModal
  const formatDateRange = (startDate, endDate) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    // Use dayjs internationalization directly
    if (start.month() === end.month()) {
      return `${start.format(
        "MMM"
      )} ${start.date()}-${end.date()}, ${start.year()}`;
    } else {
      return `${start.format("MMM")} ${start.date()} - ${end.format(
        "MMM"
      )} ${end.date()}, ${start.year()}`;
    }
  };

  useEffect(() => {
    const loadCampaign = async () => {
      try {
        setIsLoading(true);

        // Fetch all campaign details in a single request
        const response = await getCampaignDetailsBySlug(campaignSlug);
        const { campaign: campaignData, trips: tripsData } = response.data;

        // Add earlyAccessTiers to the campaign data
        const campaignWithTiers = addEarlyAccessTiers(campaignData);

        // Set the campaign and trips data
        setCampaign(campaignWithTiers);
        setTrips(tripsData);

        setError(null);
      } catch (err) {
        console.error("Error loading campaign:", err);
        setError("Campaign not found");
      } finally {
        setIsLoading(false);
      }
    };

    if (campaignSlug) {
      loadCampaign();
    }
  }, [campaignSlug]);

  // Add this useEffect to calculate and set uniform card heights
  useEffect(() => {
    if (trips.length > 0 && !isLoading) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        const cardElements = document.querySelectorAll(".project-card");
        if (cardElements.length > 0) {
          // Find the tallest card
          let maxHeight = 0;
          cardElements.forEach((card) => {
            const height = card.offsetHeight;
            if (height > maxHeight) {
              maxHeight = height;
            }
          });

          // Set the height for all cards
          if (maxHeight > 0) {
            const newHeights = {};
            trips.forEach((trip) => {
              newHeights[trip.id] = `${maxHeight}px`;
            });
            setCardHeights(newHeights);
          }
        }
      }, 500);
    }
  }, [trips, isLoading]);

  const copyReferralLink = async (code) => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/welcome-discount?code=${code}&campaign=${campaign.slug}`
      );
      showToast(t("campaign.referral.copy_success"), "success");
    } catch (error) {
      console.error("Error copying text:", error);
      showToast(t("campaign.referral.copy_error"), "error");
    }
  };

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleJoinWaitlist = async (trip) => {
    if (!isAuthenticated) {
      window.location = getLoginRedirectUrl();
      return;
    }

    // Check if campaign is past launch time
    if (!isPreLaunchCampaign(campaign)) {
      showToast(
        "This campaign has already launched. Waitlist is no longer available.",
        "info"
      );
      return;
    }

    try {
      // Use the existing waitlist functionality with trip_id
      await updateWaitlist(
        trip.id, // Using trip_id now
        trip.project_id, // Still passing project_id for reference
        true, // notify
        false, // soldOut
        true // waitlist
      );

      showToast(
        "Successfully joined the waitlist! We'll notify you when early access begins.",
        "success"
      );

      // Refresh campaign data to update waitlist counts
      const response = await getCampaignDetailsBySlug(campaignSlug);
      const { campaign: campaignData, trips: tripsData } = response.data;

      // Add earlyAccessTiers to the refreshed campaign data
      const campaignWithTiers = addEarlyAccessTiers(campaignData);

      setCampaign(campaignWithTiers);
      setTrips(tripsData);
    } catch (error) {
      console.error("Error joining waitlist:", error);
      showToast(
        error.response?.data?.error ||
          "Failed to join waitlist. Please try again.",
        "error"
      );
    }
  };

  // Add handlers for showing modals
  const handleShowWaitlistModal = (trip) => {
    // Check if campaign is past launch time
    if (!isPreLaunchCampaign(campaign)) {
      showToast(
        "This campaign has already launched. Waitlist is no longer available.",
        "info"
      );
      return;
    }

    setActiveWaitlistTrip(trip);
  };

  if (error) {
    return (
      <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50 tw-flex tw-items-center tw-justify-center">
        <div className="tw-text-center tw-p-8">
          <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-4">
            {t("campaign.page.error_title")}
          </h1>
          <p className="tw-text-gray-600 tw-mb-8">{error}</p>
          <a
            href="/"
            className="tw-bg-[deepskyblue] tw-text-white tw-px-6 tw-py-3 tw-rounded-xl hover:tw-bg-[#00a6e6] tw-transition-colors"
          >
            {t("campaign.page.back_to_home")}
          </a>
        </div>
      </div>
    );
  }

  if (isLoading || !campaign) {
    return (
      <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50 tw-flex tw-items-center tw-justify-center">
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
          <Loader className="tw-w-8 tw-h-8 tw-text-[deepskyblue] tw-animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-blue-50">
      <StickyHeader />
      {campaign && <SaleTimer campaign={campaign} />}

      {/* Campaign Title Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="tw-absolute tw-top-1/3 tw-transform -tw-translate-y-1/2 tw-left-0 tw-right-0 tw-mx-auto tw-max-w-3xl tw-px-4 tw-z-10"
      >
        <h1 className="tw-text-[22px] md:tw-text-5xl tw-font-extrabold tw-text-white tw-text-center">
          {tPromotions[campaign.id]?.name || campaign?.title}
        </h1>
        <p className="tw-text-[12px] md:tw-text-2xl tw-font-semibold tw-text-white tw-text-center">
          {tPromotions[campaign.id]?.description || campaign?.description}
        </p>
        {/* Countdown Timer Display */}
        {campaign && isPreLaunchCampaign(campaign) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="tw-mt-4 tw-flex tw-justify-center tw-items-center tw-gap-2"
          >
            <div className="tw-bg-black/30 tw-backdrop-blur-sm tw-rounded-full tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-2">
              <Clock className="tw-w-4 tw-h-4 tw-text-white" />
              <span className="tw-text-white tw-text-sm md:tw-text-base tw-font-medium">
                {campaign.launch_time
                  ? `${t(
                      "campaign.page.booking_opens"
                    )} ${convertUTCToLocalDateTimeString(campaign.launch_time)}`
                  : campaign.publicSaleStartTime
                  ? `${t(
                      "campaign.page.booking_opens"
                    )} ${convertUTCToLocalDateTimeString(
                      campaign.publicSaleStartTime
                    )}`
                  : "Coming soon"}
              </span>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Hero Video/Image - Responsive sizing and overlay */}
      <div className="tw-relative tw-w-full tw-h-[calc(100vh)]">
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-black/10 tw-to-black/10"></div>

        {isDesktop && (
          <>
            <AutoPlaySilentVideo
              className="md:tw-block tw-w-full tw-h-full tw-object-cover tw-object-center"
              video={landingVideoDesktop}
            />
          </>
        )}

        {isMobile && (
          <>
            <AutoPlaySilentVideo
              className="tw-w-full tw-h-full tw-object-cover tw-object-center"
              video={landingVideoMobile}
            />
          </>
        )}
      </div>

      <div className="tw-pt-16 tw-pb-16">
        <div className="tw-max-w-4xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
          {/* Campaign Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="tw-mb-8 tw-text-center"
          >
            <h1 className="tw-text-4xl tw-font-bold tw-bg-gradient-to-r tw-from-[deepskyblue] tw-to-blue-600 tw-bg-clip-text tw-text-transparent tw-mb-4">
              {tPromotions[campaign.id]?.name || campaign?.title}
            </h1>
            <p className="tw-text-gray-600 tw-text-lg">
              {tPromotions[campaign.id]?.description || campaign?.description}
            </p>
          </motion.div>

          {/* Vertically stacked CampaignExplainer and ReferralProgress */}
          <div className="tw-space-y-8">
            <CampaignExplainer campaign={campaign} community={community} />
            {campaign && (
              <>
                {/* Only show ReferralProgress when campaign is not live yet */}
                {isPreLaunchCampaign(campaign) && (
                  <ReferralProgress
                    campaign={campaign}
                    onCopyLink={copyReferralLink}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Projects Grid - Full width */}
        <div
          id="available-trips"
          className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-mt-16"
        >
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-8 tw-text-center"
          >
            {t("campaign.page.available_destinations")}
          </motion.h2>

          {isLoading ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Loader className="tw-w-8 tw-h-8 tw-text-[deepskyblue]" />
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="tw-mt-4 tw-text-gray-500"
              >
                {t("campaign.page.loading")}
              </motion.p>
            </div>
          ) : trips.length > 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="tw-grid md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6 tw-transform-gpu"
            >
              {trips.map((trip, index) => (
                <motion.div
                  key={trip.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.3,
                    ease: "easeOut",
                    delay: Math.min(index * 0.1, 0.8),
                  }}
                  className="trip-card"
                >
                  <TripCard
                    trip={trip}
                    onShowToast={showToast}
                    onJoinWaitlist={() => handleJoinWaitlist(trip)}
                    cardHeight={cardHeights[trip.id] || "auto"}
                    onShowWaitlistModal={handleShowWaitlistModal}
                    campaign={campaign}
                    promotion={promotion}
                  />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <EmptyTripsState campaign={campaign} />
          )}
        </div>
      </div>

      <ScrollIndicator />

      <AnimatePresence mode="wait">
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {activeWaitlistTrip && isPreLaunchCampaign(campaign) && (
          <WaitlistModal
            key="waitlist-modal"
            waitlisters={activeWaitlistTrip.waitlisters}
            totalCount={activeWaitlistTrip.waitlistCount}
            tripTitle={activeWaitlistTrip.title}
            onClose={() => setActiveWaitlistTrip(null)}
            onShowToast={showToast}
            trip={activeWaitlistTrip}
            onJoinWaitlist={() => handleJoinWaitlist(activeWaitlistTrip)}
          />
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default Campaign;

const ReferralProgress = ({ campaign, onCopyLink }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user } = useIsAuthenticated();
  const [isSharePopoverOpen, setIsSharePopoverOpen] = useState(false);
  const sharePopoverRef = useRef(null);
  const shareButtonRef = useRef(null);

  // Handle clicks outside the popover
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSharePopoverOpen &&
        sharePopoverRef.current &&
        !sharePopoverRef.current.contains(event.target) &&
        shareButtonRef.current &&
        !shareButtonRef.current.contains(event.target)
      ) {
        setIsSharePopoverOpen(false);
      }
    };

    if (isSharePopoverOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSharePopoverOpen]);

  // Handle share action and close popover
  const handleShare = async (shareMethod) => {
    if (shareMethod === "navigator" && navigator.share) {
      try {
        await navigator.share({
          title: t("campaign.referral.share_title"),
          text: t("campaign.referral.share_text"),
          url: `${window.location.origin}/welcome-discount?code=${user.id}&campaign=${campaign.slug}`,
        });
      } catch (err) {
        console.log(err);
      }
    } else if (shareMethod === "copy") {
      onCopyLink(user.id);
    }

    // Close popover after sharing
    setIsSharePopoverOpen(false);
  };

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="tw-bg-white tw-rounded-3xl tw-shadow-xl tw-overflow-hidden"
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-p-12">
          <Loader className="tw-w-8 tw-h-8 tw-text-[deepskyblue] tw-animate-spin" />
        </div>
      </motion.div>
    );
  }

  if (!isAuthenticated) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="tw-bg-white tw-rounded-3xl tw-shadow-xl tw-overflow-hidden"
      >
        <div className="tw-bg-gradient-to-r tw-from-emerald-400 tw-to-sky-500 tw-px-8 tw-py-10 tw-relative">
          <div className="tw-absolute tw-inset-0 tw-bg-grid-white/[0.2] tw-opacity-20" />
          <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-text-center">
            <h2 className="tw-text-3xl tw-font-bold tw-text-white tw-mb-6">
              {t("campaign.referral.signin_title")}
            </h2>
            <p className="tw-text-white/90 tw-mb-8">
              {t("campaign.referral.signin_subtitle")}
            </p>
            <motion.a
              href={`/login?reroute=${window.location.pathname}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="tw-bg-white tw-text-[deepskyblue] tw-px-8 tw-py-3 tw-rounded-xl tw-font-medium tw-shadow-lg hover:tw-shadow-xl tw-transition-shadow"
            >
              {t("campaign.referral.signin_button")}
            </motion.a>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="tw-bg-white tw-rounded-3xl tw-shadow-xl tw-overflow-hidden"
    >
      {/* Header Section with Gradient Background */}
      <div className="tw-bg-gradient-to-r tw-from-emerald-400 tw-to-sky-500 tw-px-8 tw-py-10 tw-relative">
        <div className="tw-absolute tw-inset-0 tw-bg-grid-white/[0.2] tw-opacity-20" />
        <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring" }}
            className="tw-bg-white/20 tw-backdrop-blur-sm tw-text-white tw-px-6 tw-py-2 tw-rounded-full tw-text-sm tw-font-semibold tw-mb-6"
          >
            {t("campaign.referral_progress.your_progress")}
          </motion.div>
          <h2 className="tw-text-3xl tw-font-bold tw-text-white tw-mb-6">
            {campaign.earlyAccessTiers &&
            campaign.earlyAccessTiers.find((tier) => !tier.achieved)
              ? `${t("campaign.referral_progress.more_friends", {
                  count:
                    campaign.earlyAccessTiers[0].requiredReferrals -
                    campaign.userReferrals,
                })}`
              : t("campaign.referral_progress.early_access_unlocked")}
          </h2>

          {/* Progress Bar */}
          <div className="tw-w-full tw-max-w-md">
            <div className="tw-relative tw-h-4 tw-bg-white/20 tw-rounded-full tw-overflow-hidden tw-shadow-inner">
              <motion.div
                initial={{ width: 0 }}
                animate={{
                  width: `${Math.min(
                    (campaign.userReferrals /
                      campaign.earlyAccessTiers[0].requiredReferrals) *
                      100,
                    100
                  )}%`,
                }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="tw-absolute tw-h-full tw-bg-white tw-rounded-full tw-shadow-lg"
              />
            </div>
            <div className="tw-flex tw-justify-between tw-mt-3 tw-text-white/90">
              <span className="tw-text-sm tw-font-medium">
                {t("campaign.referral_progress.normal_access")}
              </span>
              <span className="tw-text-sm tw-font-medium">
                {t("campaign.referral_progress.early_access")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="tw-px-8 tw-py-10">
        {/* Referral Link - Enhanced Modern Design with Share Popover */}
        <div className="tw-mb-8 tw-relative">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
            <div className="tw-flex tw-items-center tw-space-x-2">
              <LinkIcon className="tw-w-4 tw-h-4 tw-text-sky-500" />
              <span className="tw-font-medium tw-text-gray-700 tw-text-sm">
                {t("campaign.referral_progress.your_referral_link")}
              </span>
            </div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="tw-text-xs tw-font-medium tw-text-sky-600 tw-bg-sky-50 tw-px-3 tw-py-1 tw-rounded-full tw-flex tw-items-center"
            >
              <Gift className="tw-w-3.5 tw-h-3.5 tw-mr-1" />
              {t("campaign.referral_progress.earn_rewards")}
            </motion.div>
          </div>

          {/* Restructured to fix overflow issue */}
          <div className="tw-flex tw-bg-white tw-rounded-xl tw-border tw-border-gray-200">
            {/* Referral Link */}
            <div className="tw-flex-1 tw-overflow-hidden">
              <div
                onClick={() => onCopyLink(user.id)}
                className="tw-w-full tw-h-full tw-flex tw-items-center tw-px-4 tw-py-3.5 tw-cursor-pointer tw-transition-colors hover:tw-bg-gray-50"
              >
                <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-sky-100 tw-flex tw-items-center tw-justify-center tw-mr-3 tw-flex-shrink-0">
                  <LinkIcon className="tw-w-4 tw-h-4 tw-text-sky-500" />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-text-xs tw-text-gray-500 tw-mb-0.5">
                    {t("campaign.referral_progress.click_to_copy")}
                  </div>
                  <div className="tw-text-gray-700 tw-text-sm tw-font-medium tw-truncate">
                    {`${window.location.origin}/welcome-discount?code=${user.id}&campaign=${campaign.slug}`}
                  </div>
                </div>
              </div>
            </div>

            {/* Share Button - Now outside the overflow hidden container */}
            <button
              ref={shareButtonRef}
              onClick={() => onCopyLink(user.id)}
              className="tw-px-4 tw-border-l tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-bg-gray-50 tw-transition-colors hover:tw-bg-sky-50 tw-rounded-r-xl"
            >
              <div className="tw-flex tw-flex-col tw-items-center">
                <Share2 className="tw-w-5 tw-h-5 tw-text-sky-500" />
                <span className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mt-1">
                  {t("campaign.referral.share")}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Referred Users Section */}
        {campaign.referredUsers && campaign.referredUsers.length > 0 && (
          <div className="tw-mt-10">
            <div className="tw-flex tw-items-center tw-space-x-2 tw-mb-6">
              <Users className="tw-w-5 tw-h-5 tw-text-sky-500" />
              <span className="tw-font-semibold tw-text-gray-900">
                {t("campaign.referral_progress.successfully_referred")}
              </span>
            </div>

            <div className="tw-space-y-3">
              {campaign.referredUsers.map((user, index) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="tw-flex tw-items-center tw-p-3 tw-bg-gray-50 tw-rounded-xl hover:tw-bg-gray-100 tw-transition-colors"
                >
                  <ImageResizable
                    size="xs"
                    src={user.avatar_url}
                    alt={user.name}
                    className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover tw-mr-3 tw-border-2 tw-border-white"
                  />
                  <div className="tw-flex-1">
                    <div className="tw-font-medium tw-text-gray-900">
                      {user.name}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500">
                      {t("campaign.referral_progress.referral_date")}{" "}
                      {new Date(user.joinedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="tw-bg-emerald-100 tw-text-emerald-600 tw-text-xs tw-font-medium tw-px-2 tw-py-1 tw-rounded-full">
                    {t("campaign.referral_progress.referred_by_you")}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {campaign.referredUsers && campaign.referredUsers.length === 0 && (
          <div className="tw-mt-10">
            <div className="tw-flex tw-items-center tw-space-x-2 tw-mb-6">
              <Users className="tw-w-5 tw-h-5 tw-text-sky-500" />
              <span className="tw-font-semibold tw-text-gray-900">
                {t("campaign.referral_progress.successfully_referred")}
              </span>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-flex tw-flex-col tw-items-center tw-text-center"
            >
              <div className="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
                <Users className="tw-w-8 tw-h-8 tw-text-gray-400" />
              </div>
              <h4 className="tw-font-medium tw-text-gray-700 tw-mb-2">
                {t("campaign.referral_progress.no_friends_joined")}
              </h4>
              <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                {t(
                  "campaign.referral_progress.invite_friends_for_early_access"
                )}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onCopyLink(user.id)}
                className="tw-bg-sky-500 tw-text-white tw-text-sm tw-px-4 tw-py-2 tw-rounded-lg tw-font-medium tw-flex tw-items-center"
              >
                <Copy className="tw-w-4 tw-h-4 tw-mr-2" />
                {t("campaign.referral.copy_link")}
              </motion.button>
            </motion.div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
